import { z } from 'zod';
import { localeSchema, themeSchema, userRoleSchema } from './enums.js';
/**
 * Contrats d'authentification (T7a, XPHOTO-12) — source unique front + back.
 *
 * Gate de login : l'app est verrouillée derrière une session (cookie httpOnly
 * opaque, adossé à une table `sessions` révocable côté serveur). Les écrans
 * d'administration (XPHOTO-25), les accès lecture seule par sous-arbre et le
 * switch multi-comptes `/u/0/` sont déférés à un lot ultérieur (T7b).
 */
/**
 * Corps de `POST /api/auth/login`. Volontairement permissif sur le mot de passe
 * (`min(1)`) : la politique de force s'applique à la CRÉATION du compte, pas au
 * login — le contrôle ici doit rester générique (anti-énumération).
 *
 * `stayLoggedIn` (« Rester connecté ») pilote la PERSISTANCE du cookie de session :
 * `true`/omis → cookie persistant (Max-Age = TTL) survivant à la fermeture du
 * navigateur ; `false` → cookie de session (aucun Max-Age), effacé à la fermeture.
 * Plain `.optional()` (jamais `.default()`/`.transform()`) : la route appelle
 * `z.toJSONSchema(loginRequestSchema)` au boot (piège boot-crash Zod v4).
 */
export const loginRequestSchema = z.object({
    email: z.string().email().max(320),
    password: z.string().min(1).max(1024),
    stayLoggedIn: z.boolean().optional(),
});
/**
 * Utilisateur courant — réponse de `GET /api/auth/me` et de `POST
 * /api/auth/login`. Ne porte JAMAIS le hash du mot de passe. `libraryId` est la
 * bibliothèque possédée (une par utilisateur) ou `null` si aucune n'est encore
 * assignée.
 *
 * `randomSeed` : graine PERSISTÉE côté serveur de l'ordre aléatoire de la galerie
 * (`?graine=`). Stockée par utilisateur → l'ordre aléatoire est IDENTIQUE et
 * STABLE sur toutes ses machines/sessions, re-mélangé uniquement à la demande
 * (`POST /api/auth/random-seed`). `z.string()` : la valeur vient de la base
 * (backfillée pour respecter `ASSET_SEED_PATTERN`).
 *
 * ⚠ `.optional()` DÉLIBÉRÉ (ajout ADDITIF non cassant) : le serveur RENSEIGNE
 * TOUJOURS ce champ dans `me`/`login` (`SessionUser.randomSeed` est requis, non
 * nul). L'optionnalité ne concerne QUE le contrat consommé par la SPA (`apps/web`),
 * qui pourra brancher la graine serveur dans un lot ultérieur SANS que ce lot
 * backend ne casse son typecheck.
 */
export const currentUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    locale: localeSchema,
    theme: themeSchema,
    libraryId: z.uuid().nullable(),
    randomSeed: z.string().optional(),
    /**
     * La SESSION courante a été ouverte par un LIEN D'ACCÈS communautaire (audit A6),
     * et non par un login mot de passe. Décrit la session, pas le compte : le MÊME
     * compte peut être joint par les deux voies.
     *
     * Utilité côté SPA : masquer les affordances impraticables pour un porteur de lien.
     * Au premier chef « Changer le mot de passe » — il n'a jamais connu le mot de passe
     * du compte partagé et ne peut donc pas remplir le champ « mot de passe actuel »
     * qu'exige `POST /api/auth/change-password`. C'était un cul-de-sac silencieux.
     *
     * `.optional()` (ajout ADDITIF non cassant) : `GET /api/auth/me` le renseigne
     * toujours, mais un client plus ancien qui l'ignore reste valide. Absent ⇒ traiter
     * comme `false` (le comportement historique : on affiche le formulaire).
     */
    viaSessionLink: z.boolean().optional(),
});
/**
 * Réponse de `POST /api/auth/random-seed` — régénère + persiste la graine de
 * l'ordre aléatoire de l'utilisateur courant et renvoie la NOUVELLE graine (au
 * format `ASSET_SEED_PATTERN`, générée côté serveur).
 */
export const reshuffleRandomSeedResultSchema = z.object({
    randomSeed: z.string(),
});
/**
 * Réponse de `POST /api/auth/login` (multi-login) — l'utilisateur connecté ET le
 * `slot` (petit entier) qui vient de lui être attribué dans le faisceau du
 * navigateur. Le client mémorise ce slot (par onglet) et le renvoie ensuite via
 * l'en-tête `X-Active-Slot`.
 */
export const loginResponseSchema = z.object({
    slot: z.number().int().min(0),
    user: currentUserSchema,
});
/**
 * Un compte EMPILÉ dans le faisceau — vue légère pour le sélecteur de comptes
 * (`GET /api/auth/accounts`). Ne porte pas les préférences (locale/thème) : c'est
 * une entrée de menu, pas le profil actif.
 */
export const sessionAccountSchema = z.object({
    slot: z.number().int().min(0),
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    libraryId: z.uuid().nullable(),
});
/** Réponse de `GET /api/auth/accounts` — les comptes actuellement connectés. */
export const accountsResponseSchema = z.object({
    accounts: z.array(sessionAccountSchema),
});
/**
 * Corps de `POST /api/auth/logout`. `all: true` déconnecte TOUS les comptes
 * empilés (vide le faisceau + révoque toutes les sessions de l'utilisateur) ;
 * sinon, seul le compte du slot actif (en-tête `X-Active-Slot`) est déconnecté.
 */
export const logoutRequestSchema = z.object({
    all: z.boolean().optional().default(false),
});
/**
 * Corps de `POST /api/auth/change-password`. La politique de force (`min(8)`)
 * s'applique au NOUVEAU mot de passe, comme à la création de compte côté admin ;
 * le mot de passe actuel reste permissif (`min(1)`) — il n'est que vérifié.
 */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(1024),
    newPassword: z.string().min(8).max(1024),
});
//# sourceMappingURL=auth.js.map